import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Star } from "lucide-react";
import { MarketingLayout } from "@/components/MarketingLayout";
import { CustomerGate } from "@/components/customer/CustomerGate";
import { SetupChecklist } from "@/components/customer/SetupChecklist";
import { BrandSelect } from "@/components/customer/BrandSelect";
import { TrialBanner } from "@/components/customer/TrialBanner";
import { ViolationBadge } from "@/components/audit/ViolationBadge";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import type { BrandAmazonAccess } from "@/lib/amazonAccess";

type BrandSummary = BrandAmazonAccess & {
  id: string;
  name: string;
  asinCount: number;
  flaggedThisMonth: number;
  takedownsSubmitted: number;
  removed: number;
};

type BrandReview = {
  id: string;
  asin: string;
  productTitle: string | null;
  rating: number;
  title: string | null;
  body: string;
  reviewerName: string | null;
  verifiedPurchase: boolean;
  createdAt: string;
  classification: {
    violations: { type: string; confidence?: number }[] | null;
    rationale: string | null;
    actionStatus: string;
    submittedAt: string | null;
    removedAt: string | null;
  } | null;
};

type TrialStatus = {
  tier: string;
  status: string;
  trialEndsAt: string | null;
  skuCap: number | null;
  scrapeCadence: string;
  currentPeriodEnd: string | null;
  monitoredAsinCount: number;
};

type Status = "queued" | "scraping" | "classifying" | "completed" | "failed";

type AuditRow = {
  id: string;
  email: string;
  status: Status;
  inputAsins: string[];
  scrapedReviewCount: number;
  flaggedCount: number;
  createdAt: string;
};

function statusLabel(s: Status): string {
  if (s === "completed") return "Completed";
  if (s === "failed") return "Failed";
  return "In queue";
}

type CustomerInfo = { email: string };

function Inner({ customer }: { customer: CustomerInfo }) {
  const navigate = useNavigate();
  const [audits, setAudits] = useState<AuditRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [brands, setBrands] = useState<BrandSummary[] | null>(null);
  const [trial, setTrial] = useState<TrialStatus | null>(null);

  useEffect(() => {
    let cancelled = false;
    api<{ audits: AuditRow[] }>("/customer/audits").then((res) => {
      if (cancelled) return;
      if (!res.ok) {
        setError(res.error ?? "Failed to load audits.");
        return;
      }
      setAudits(res.data?.audits ?? []);
    });
    api<{ brands: BrandSummary[] }>("/customer/brands").then((res) => {
      if (cancelled) return;
      setBrands(res.data?.brands ?? []);
    });
    api<{ subscription: TrialStatus | null }>("/customer/trial-status").then((res) => {
      if (cancelled) return;
      setTrial(res.data?.subscription ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  async function onSignOut() {
    const res = await api<{ ok: true; logoutUrl?: string }>("/customer/logout", {
      method: "POST",
    });
    if (res.data?.logoutUrl) {
      window.location.assign(res.data.logoutUrl);
      return;
    }
    navigate("/login", { replace: true });
  }

  return (
    <div className="mx-auto w-full max-w-4xl px-6 py-12">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
            Your audits
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            Signed in as <strong>{customer.email}</strong>
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={onSignOut}>
          Sign out
        </Button>
      </div>

      {brands && brands.length > 0 && (
        <BrandSection brands={brands} trial={trial} />
      )}

      <div className="mt-6">
        <SetupChecklist brands={brands ?? []} />
      </div>

      <div className="mt-8 rounded-xl border border-slate-200 bg-white">
        {error && (
          <p className="p-6 text-sm text-destructive">{error}</p>
        )}
        {!audits && !error && (
          <div className="flex items-center gap-2 p-6 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}
        {audits && audits.length === 0 && !error && (
          <div className="p-8 text-center">
            <p className="text-sm text-slate-600">
              No audits yet on this email.
            </p>
            <Link
              to="/audit"
              className="mt-4 inline-block rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
            >
              Run your first free audit
            </Link>
          </div>
        )}
        {audits && audits.length > 0 && (
          <ul className="divide-y divide-slate-200">
            {audits.map((a) => (
              <li key={a.id}>
                <Link
                  to={`/dashboard/audits/${a.id}`}
                  className="flex items-center justify-between gap-4 px-6 py-4 hover:bg-slate-50"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium text-slate-900">
                        {a.inputAsins.length > 0
                          ? `${a.inputAsins.length} ASIN${a.inputAsins.length === 1 ? "" : "s"}`
                          : "Storefront"}
                      </span>
                      <span className="text-slate-400">·</span>
                      <span className="text-slate-500">
                        {a.createdAt.slice(0, 10)}
                      </span>
                    </div>
                    <div className="mt-1 truncate text-xs text-slate-500">
                      {a.inputAsins.slice(0, 3).join(", ")}
                      {a.inputAsins.length > 3 ? ", …" : ""}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-3 text-xs">
                    <span className="text-slate-500">{statusLabel(a.status)}</span>
                    {a.flaggedCount > 0 && (
                      <span className="rounded-full bg-amber-50 px-2 py-0.5 font-medium text-amber-800 ring-1 ring-inset ring-amber-200">
                        {a.flaggedCount} flagged
                      </span>
                    )}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default function CustomerDashboard() {
  return (
    <MarketingLayout>
      <CustomerGate>
        {(customer) => (
          <Inner
            customer={{
              email: customer.email,
            }}
          />
        )}
      </CustomerGate>
    </MarketingLayout>
  );
}

function BrandSection({
  brands,
  trial,
}: {
  brands: BrandSummary[];
  trial: TrialStatus | null;
}) {
  const [activeBrandId, setActiveBrandId] = useState(brands[0]?.id ?? "");
  const [reviews, setReviews] = useState<BrandReview[] | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const active = brands.find((b) => b.id === activeBrandId) ?? brands[0];

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    setReviews(null);
    setExpanded(null);
    api<{ reviews: BrandReview[] }>(`/customer/brands/${active.id}/reviews`).then(
      (res) => {
        if (cancelled) return;
        setReviews(res.data?.reviews ?? []);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [active]);

  if (!active) return null;

  return (
    <div className="mt-6 space-y-6">
      {trial?.status === "trialing" && (
        <TrialBanner
          trialEndsAt={trial.trialEndsAt}
          monitoredAsinCount={trial.monitoredAsinCount}
          skuCap={trial.skuCap}
          scrapeCadence={trial.scrapeCadence}
        />
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-sm font-medium text-slate-500">Brand</div>
          <BrandSelect
            value={active.id}
            onChange={setActiveBrandId}
            brands={brands.map((b) => ({ id: b.id, name: b.name }))}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiTile label="Products monitored" value={active.asinCount} />
        <KpiTile label="Flagged this month" value={active.flaggedThisMonth} />
        <KpiTile label="Takedowns submitted" value={active.takedownsSubmitted} />
        <KpiTile label="Removed" value={active.removed} accent />
      </div>

      <section>
        <h2 className="text-lg font-semibold text-slate-900">
          Recent flagged reviews
        </h2>
        <div className="mt-3 divide-y divide-slate-200 rounded-2xl border border-slate-200 bg-white">
          {reviews === null && (
            <div className="flex items-center gap-2 px-5 py-6 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          )}
          {reviews && reviews.length === 0 && (
            <div className="px-5 py-8 text-center text-sm text-slate-600">
              Nothing flagged yet for this brand. Reviews are scanned weekly.
            </div>
          )}
          {reviews?.map((r) => (
            <BrandReviewRow
              key={r.id}
              review={r}
              expanded={expanded === r.id}
              onToggle={() =>
                setExpanded((cur) => (cur === r.id ? null : r.id))
              }
            />
          ))}
        </div>
      </section>
    </div>
  );
}

function KpiTile({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: boolean;
}) {
  return (
    <div
      className={
        accent
          ? "rounded-2xl border border-emerald-200 bg-emerald-50 p-5"
          : "rounded-2xl border border-slate-200 bg-white p-5"
      }
    >
      <div
        className={
          accent ? "text-xs font-medium text-emerald-700" : "text-xs font-medium text-slate-500"
        }
      >
        {label}
      </div>
      <div
        className={
          accent
            ? "mt-2 text-3xl font-semibold tracking-tight text-emerald-900"
            : "mt-2 text-3xl font-semibold tracking-tight text-slate-900"
        }
      >
        {value}
      </div>
    </div>
  );
}

const ACTION_STATUS_LABEL: Record<string, { label: string; cls: string }> = {
  none: { label: "Awaiting authorization", cls: "bg-slate-100 text-slate-700 ring-slate-200" },
  watching: { label: "Watching", cls: "bg-blue-50 text-blue-700 ring-blue-200" },
  authorized: { label: "Authorized · awaiting filing", cls: "bg-sky-50 text-sky-700 ring-sky-200" },
  submitted: { label: "Submitted to Amazon", cls: "bg-amber-50 text-amber-800 ring-amber-200" },
  removed: { label: "Removed", cls: "bg-emerald-50 text-emerald-800 ring-emerald-200" },
  rejected: { label: "Rejected", cls: "bg-red-50 text-red-800 ring-red-200" },
};

function BrandReviewRow({
  review,
  expanded,
  onToggle,
}: {
  review: BrandReview;
  expanded: boolean;
  onToggle: () => void;
}) {
  const status = review.classification?.actionStatus ?? "none";
  const meta = ACTION_STATUS_LABEL[status] ?? ACTION_STATUS_LABEL.none;
  const violations = (review.classification?.violations as { type: string }[] | null) ?? [];
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-start gap-4 px-5 py-4 text-left hover:bg-slate-50"
      >
        <div className="flex w-32 shrink-0 flex-col items-start gap-1">
          <StarRating rating={review.rating} />
          <span className="font-mono text-[11px] text-slate-500">{review.asin}</span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-3">
            <span className="font-medium text-slate-900">
              {review.productTitle || review.asin}
            </span>
            <span className="text-xs text-slate-500">
              {review.verifiedPurchase ? "Verified" : "Unverified"}
              {review.reviewerName ? ` · ${review.reviewerName}` : ""}
            </span>
          </div>
          <p className="mt-1 line-clamp-1 text-sm text-slate-600">
            {review.title ? `"${review.title}" — ` : ""}
            {review.body.slice(0, 140)}
            {review.body.length > 140 ? "…" : ""}
          </p>
          {violations.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {violations.map((v, i) => (
                <ViolationBadge key={i} type={v.type} />
              ))}
            </div>
          )}
        </div>
        <div className="ml-2 flex shrink-0 flex-col items-end gap-1">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${meta.cls}`}
          >
            {meta.label}
          </span>
          <span className="text-xs text-slate-500">
            {new Date(review.createdAt).toLocaleDateString()}
          </span>
        </div>
      </button>
      {expanded && (
        <div className="border-t border-slate-100 bg-slate-50 px-5 py-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Full review
              </div>
              <p className="mt-2 text-sm text-slate-700">"{review.body}"</p>
              {review.classification?.rationale && (
                <>
                  <div className="mt-4 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Why we flagged this
                  </div>
                  <p className="mt-2 text-sm text-slate-700">
                    {review.classification.rationale}
                  </p>
                </>
              )}
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Take action
              </div>
              <p className="mt-2 text-sm text-slate-600">
                Authorize takedowns from the new dashboard. Submissions are
                included with your monitoring plan.
              </p>
              <Link
                to="/dashboard"
                className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
              >
                Open new dashboard →
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StarRating({ rating }: { rating: number }) {
  const safe = Math.max(0, Math.min(5, Math.round(rating)));
  return (
    <span
      className="inline-flex items-center gap-0.5"
      aria-label={`${safe} out of 5 stars`}
    >
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`h-3.5 w-3.5 ${
            n <= safe ? "fill-amber-400 text-amber-400" : "fill-slate-200 text-slate-200"
          }`}
        />
      ))}
    </span>
  );
}
